import { WhistleIcon } from '../../components/WhistleIcon';
import type { SportEventTypeConfig } from '../types';
import { COMMON_EVENT_TYPES } from '../common';

/**
 * NBA play-by-play event catalog. Listed in the order that mirrors the
 * panel's natural reading flow (scoring events first, then rebounds, then
 * fouls/turnovers/admin).
 *
 * Notes:
 *   - made/missed remain separate types (they drive scoring, the highlight
 *     row, points and descriptions) but are merged into ONE filter pill per
 *     shot category via `filterGroup`: a single "2-Pointer" / "3-Pointer" /
 *     "Free Throw" pill surfaces both the made and missed attempts. The made
 *     type owns the pill (showFilterPill: true); the missed type joins the
 *     group with showFilterPill: false.
 *   - the three foul types (foul_personal, foul_offensive, foul_drawn) are
 *     merged into ONE "Foul" pill via filterGroup: 'foul'. foul_personal owns
 *     the pill; the other two join with showFilterPill: false. Selecting it
 *     surfaces both sides of the whistle — the committed fouls
 *     (personal/offensive) and the paired drawn-foul rows. They remain
 *     separate types so the whistle icon and row text stay per-type.
 *   - substitution defaults to showFilterPill:false because subs arrive in
 *     batches of 5+ at the same nanosecond and would dominate the timeline.
 *   - cross-sport types (`custom`, and today `period_start`/`period_end`) are
 *     appended from COMMON_EVENT_TYPES. They MUST be part of the active sport
 *     module: the panel's display filter is scoped to the current game's
 *     module (useActiveSportEventTypes -> backendEventTypes -> setFilter), so
 *     a type declared only under NHL gets filtered out of an NBA timeline —
 *     which is exactly why NBA custom events used to vanish.
 */
export const NBA_EVENT_TYPES: ReadonlyArray<SportEventTypeConfig> = [
  ...COMMON_EVENT_TYPES,
  {
    type: 'basket_made_3pt',
    fetchFromBackend: true,
    showFilterPill: true,
    filterGroup: 'basket_3pt',
    displayName: '3-Pointer',
    description: 'Made 3-point field goal',
  },
  {
    type: 'basket_made_2pt',
    fetchFromBackend: true,
    showFilterPill: true,
    filterGroup: 'basket_2pt',
    displayName: '2-Pointer',
    description: 'Made 2-point field goal',
  },
  {
    type: 'basket_missed_3pt',
    fetchFromBackend: true,
    showFilterPill: false,
    filterGroup: 'basket_3pt',
    displayName: '3-Pointer Missed',
    description: 'Missed 3-point field goal',
  },
  {
    type: 'basket_missed_2pt',
    fetchFromBackend: true,
    showFilterPill: false,
    filterGroup: 'basket_2pt',
    displayName: '2-Pointer Missed',
    description: 'Missed 2-point field goal',
  },
  {
    type: 'free_throw_made',
    fetchFromBackend: true,
    showFilterPill: true,
    filterGroup: 'free_throw',
    displayName: 'Free Throw',
    description: 'Made free throw',
  },
  {
    type: 'free_throw_missed',
    fetchFromBackend: true,
    showFilterPill: false,
    filterGroup: 'free_throw',
    displayName: 'Free Throw Missed',
    description: 'Missed free throw',
  },
  {
    type: 'rebound_defensive',
    fetchFromBackend: true,
    showFilterPill: true,
    filterGroup: 'rebound',
    displayName: 'Rebounds',
    description: 'Defensive rebound',
  },
  {
    type: 'rebound_offensive',
    fetchFromBackend: true,
    showFilterPill: false,
    filterGroup: 'rebound',
    displayName: 'Offensive Rebound',
    description: 'Offensive rebound',
  },
  {
    type: 'foul_personal',
    fetchFromBackend: true,
    showFilterPill: true,
    filterGroup: 'foul',
    displayName: 'Foul',
    description: 'Personal foul',
    // No icon: a personal foul is attributed to the committing player's team
    // (resolved from `committed_by` in PlayByPlayPanel), so the row shows that
    // team's logo. RegularEventItem renders the icon in place of the logo when
    // present, so an icon here would hide the team affiliation behind a whistle.
  },
  {
    type: 'foul_offensive',
    fetchFromBackend: true,
    showFilterPill: false,
    filterGroup: 'foul',
    displayName: 'Offensive Foul',
    description: 'Offensive foul',
    icon: WhistleIcon,
  },
  {
    type: 'foul_drawn',
    fetchFromBackend: true,
    showFilterPill: false,
    filterGroup: 'foul',
    displayName: 'Foul Drawn',
    description: 'Foul drawn',
  },
  {
    type: 'turnover',
    fetchFromBackend: true,
    showFilterPill: true,
    displayName: 'Turnover',
    description: 'Turnover',
  },
  {
    type: 'substitution',
    fetchFromBackend: true,
    showFilterPill: false,
    displayName: 'Substitution',
    description: 'Substitution',
  },
  {
    type: 'violation',
    fetchFromBackend: true,
    showFilterPill: false,
    displayName: 'Violation',
    description: 'Rule violation',
    icon: WhistleIcon,
  },
];
